import React, { FC, useMemo } from 'react';
import { useTheme } from '@chakra-ui/react';
import { EyeOff, Heart, Link as LinkIcon, Send } from 'react-feather';
import { showGalleryToast, useDismissGalleryToast } from 'ts/client/gallery/galleryStandaloneToast';
import Toast from 'ts/common/components/Toast';
import { TagEnabledIcon } from 'ts/common/components/icons';

export const ALL_TOAST_TYPES = ['favorite', 'favorites_sent', 'label', 'hide', 'copyLink'] as const;

type GalleryToastProps = Omit<React.ComponentProps<typeof Toast>, 'onClose'>;

/** Closes just this toast, rather than every toast in the stack. */
const DismissibleToast: FC<GalleryToastProps> = (props) => {
    const dismiss = useDismissGalleryToast();

    return <Toast onClose={dismiss} {...props} />;
};

export interface UseToastInput {
    type: (typeof ALL_TOAST_TYPES)[number];
    translateFilter: SpAngularJs.ITranslateFilter;
}

const useToast = ({ type, translateFilter: translate }: UseToastInput) => {
    const chakraTheme = useTheme();

    const toastProps = useMemo(() => {
        switch (type) {
            case 'favorite':
                return {
                    icon: Heart,
                    iconFill: 'brand.primary',
                    title: translate('photoAddedToFavorites'),
                    description: translate('photoAddedToFavoritesDescription')
                };
            case 'favorites_sent':
                return {
                    icon: Send,
                    iconFill: 'brand.primary',
                    title: translate('favoritePhotosSent'),
                    description: translate('favoritePhotosSentDescription')
                };
            case 'label':
                return {
                    icon: TagEnabledIcon,
                    title: translate('photoLabeled'),
                    description: translate('photoLabeledDescription')
                };
            case 'hide':
                return {
                    icon: EyeOff,
                    title: translate('photoHidden'),
                    description: translate('photoHiddenDescription')
                };
            case 'copyLink':
                return {
                    icon: LinkIcon,
                    title: translate('copiedLink')
                };
            default:
                throw new Error(`Invalid ToastType: ${type}`);
        }
    }, [translate, type]);

    const toast = () => {
        showGalleryToast(
            chakraTheme as Record<string, unknown>,
            <DismissibleToast {...toastProps} />
        );
    };

    return toast;
};

export default useToast;
